<mat-toolbar class="header">
  <div class="header-left">
    <a href="https://www.columbia.edu/" 
       target="_blank" 
       rel="noopener noreferrer"
       matTooltip="Visit Columbia University homepage (opens in new tab)"
       aria-label="Visit Columbia University homepage, opens in new tab"
       class="columbia-logo-link">
      <img src="cu_logo_white.png" alt="Columbia University in the City of New York"/>
    </a>
  </div>
  <div class="header-center">
    <h1 class="app-title">Columbia Academic Personnel System</h1>
  </div>
  
  <!-- Impersonation Indicator (read-only "view as" mode) -->
  @if (isImpersonating()) {
    <div class="impersonation-indicator">
      <mat-icon class="impersonation-icon">visibility</mat-icon>
      <div class="impersonation-text">
        <span class="impersonation-label">Viewing as (read-only)</span>
        <span class="impersonation-name">{{ impersonatedUserName() }}</span>
      </div>
      <button mat-stroked-button
              class="stop-impersonating-button"
              (click)="stopImpersonating()"
              matTooltip="Return to your own dashboard">
        <mat-icon>close</mat-icon>
        <span>Stop Impersonating</span>
      </button>
    </div>
  }
  
  <div class="header-right">
    @if (isAuthenticated()) {
      <button mat-button [matMenuTriggerFor]="userMenu" class="user-button"
              [class.user-button--degraded]="sessionDegraded()"
              [matTooltip]="sessionDegraded() ? 'Session partially expired — click to re-authenticate' : 'User menu'">
        @if (sessionDegraded()) {
          <mat-icon class="degraded-icon">warning</mat-icon>
        } @else {
          <mat-icon>account_circle</mat-icon>
        }
        <div class="user-name-role">
          <span class="user-id">{{ loggedInUserName() }}</span>
          @if (apiUserRoles().length > 0 && !rolesLoading()) {
            <span class="role-badge">{{ roleSummary() }}</span>
          }
        </div>
        <mat-icon>arrow_drop_down</mat-icon>
      </button>

      <mat-menu #userMenu="matMenu">
        <div class="user-info">
          <div class="user-label">
            <strong>{{ loggedInUserName() }}</strong>
          </div>
          @if (loggedInUserSubject()) {
            <div class="user-label">{{ loggedInUserSubject() }}</div>
          }
        </div>

        @if (sessionDegraded()) {
          <mat-divider></mat-divider>
          <div class="session-degraded-banner">
            <mat-icon>warning</mat-icon>
            <span>Identity server session expired. Re-authenticate to restore full access.</span>
          </div>
          <button mat-menu-item (click)="reAuthenticate()" class="reauth-item">
            <mat-icon>refresh</mat-icon>
            <span>Re-authenticate</span>
          </button>
        }

        <mat-divider></mat-divider>

        @if (rolesLoading()) {
          <div class="permission-info">
            <span class="permission-label">Loading roles...</span>
          </div>
        } @else if (apiUserRoles().length > 0) {
          <div class="permission-info">
            <span class="permission-section-title">Your Roles</span>

            @if (categorizedRoles().tier1.length > 0) {
              <div class="role-tier">
                @for (role of categorizedRoles().tier1; track role.id) {
                  <span class="role-chip tier-1">{{ groupDisplayNames[role.group_name] || role.group_name }}</span>
                }
              </div>
            }

            @if (categorizedRoles().tier2.length > 0) {
              <div class="role-tier">
                <span class="scope-label">{{ tier2Label() }}</span>
                @for (role of categorizedRoles().tier2; track role.id) {
                  <span class="role-chip tier-2">{{ role.reporting_unit?.name || 'Unknown RU' }}</span>
                }
              </div>
            }

            @if (categorizedRoles().tier3.length > 0) {
              <div class="role-tier">
                <span class="scope-label">{{ tier3Label() }}</span>
                @for (role of categorizedRoles().tier3; track role.id) {
                  <span class="role-chip tier-3">{{ role.department?.description || role.department?.name || 'Unknown Dept' }}</span>
                }
              </div>
            }
          </div>
        } @else {
          <div class="permission-info">
            <span class="permission-label">No roles assigned</span>
          </div>
        }

        <mat-divider></mat-divider>

        @if (showImpersonateItem()) {
          <button mat-menu-item (click)="impersonate()" matTooltip="View CAPS as another role or officer (read-only)">
            <mat-icon>visibility</mat-icon>
            <span>Impersonate (View As)</span>
          </button>
        }

        @if (canViewOwnUserDetails()) {
          <button mat-menu-item
                  [routerLink]="['/user-management', myAccountId()]"
                  matTooltip="View your own account and assigned roles">
            <mat-icon>badge</mat-icon>
            <span>My User Details</span>
          </button>
        }

        <button mat-menu-item (click)="changePermission()" matTooltip="Manage UNI account settings">
          <a href="https://www.cuit.columbia.edu/cuit/manage-my-uni" target="_blank">
            <mat-icon>account_circle</mat-icon>
            <span>Manage My UNI</span>
            <mat-icon>open_in_new</mat-icon>
          </a>
        </button>

        @if (isImpersonating()) {
          <mat-divider></mat-divider>
          <button mat-menu-item (click)="stopImpersonating()" matTooltip="Return to your own dashboard">
            <mat-icon>close</mat-icon>
            <span>Stop Impersonating {{ impersonatedUserName() }}</span>
          </button>
        }

        <button mat-menu-item (click)="signOut()" matTooltip="Sign out of the application">
          <mat-icon>logout</mat-icon>
          <span>Sign Out</span>
        </button>
      </mat-menu>
    }
  </div>
</mat-toolbar>